import { useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { toast } from "@/components/ui/toast";
import { emptyBrowseFilter, getEligibilityFromBrowseFilter, syncBrowseFilterPlatforms } from "@/utils/browseFilter";
import { analyticsService } from "../../services/analytics";
import { createBooker } from "../../services/bookers";
import { UserContext, getCampaignById, getPitchesByCampaignId, newCreateOrUpdateCampaign, updateCampaignSettings, useFreshBookers, useFreshClients, useFreshSalesTeam } from "../../services/firebase";
import { asIdList } from "../../shared/asIdList";
import { getCurrencySymbol } from "../../shared/currency";
import type { BrowseFilter, Campaign, CampaignType, Currency, Platform, User } from "../../shared/types";
import { canCreateCampaign } from "../../utils/campaignPermissions";
import { isPitchReviewer } from "../../utils/pitching/permissions";
import { missingFieldsToOpenPitching, pitchingOpenRequirementsCopy } from "../../utils/pitching/requirements";
import { applyBookerCurrency, applyCampaignToForm, buildCampaignFromForm, canSendBookerPitchDigest, countNewlyExcludedPitches, emptyFormValues, type CampaignFormMode, type CampaignFormValues } from "./campaign";

/**
 * State, load, and submit for create and settings.
 * Returns the props each section renders.
 */
export function useCampaignForm(mode: CampaignFormMode) {
    const { campaignId } = useParams<{ campaignId: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const openAfterSave = mode === "edit" && searchParams.get("open") === "1";
    const queryClient = useQueryClient();
    const authenticatedUser = useContext(UserContext);
    const isManager = Boolean(authenticatedUser?.roles?.manager || authenticatedUser?.roles?.admin);
    const isSalesOrAdmin = Boolean(authenticatedUser?.roles?.salesTeam || authenticatedUser?.roles?.admin);

    const bookersQuery = useFreshBookers({ enabled: isManager });
    const bookers = bookersQuery.flatData;
    const clientsQuery = useFreshClients({ enabled: isManager });
    const clients = clientsQuery.flatData;
    const salesTeamQuery = useFreshSalesTeam({ enabled: isManager || Boolean(authenticatedUser?.roles?.salesTeam) });
    const salesTeam = salesTeamQuery.flatData;

    const form = useForm<CampaignFormValues>({
        defaultValues: emptyFormValues(),
    });
    const {
        control,
        register,
        handleSubmit,
        reset,
        resetField,
        setValue,
        formState: { errors, isSubmitting },
    } = form;
    const campaignName = useWatch({ control, name: "campaignName" });

    const [campaign, setCampaign] = useState<Campaign>();
    const [campaignLoading, setCampaignLoading] = useState(mode === "edit");
    const [booker, setBooker] = useState<User | undefined>(
        authenticatedUser?.roles?.booker && !authenticatedUser?.roles?.manager
            ? authenticatedUser
            : undefined,
    );
    const [currency, setCurrency] = useState<Currency | undefined>("USD");
    const [currencyLockedFromBooker, setCurrencyLockedFromBooker] = useState(false);
    const [campaignType, setCampaignType] = useState<CampaignType>("Brand");
    const [platforms, setPlatforms] = useState<Platform[]>([]);
    const [eligibility, setEligibility] = useState<BrowseFilter>({
        ...emptyBrowseFilter,
    });
    const [showNotesAndRates, setShowNotesAndRates] = useState(true);
    const [sendBookerPitchDigest, setSendBookerPitchDigest] = useState(false);
    const [fileUrl, setFileUrl] = useState<string>();
    const [pitchCount, setPitchCount] = useState(0);
    const [basicsErrors, setBasicsErrors] = useState<{
        booker?: string;
        platforms?: string;
        currency?: string;
    }>({});

    const bookerLocked = mode === "edit";
    const basicsLocked = mode === "edit" && pitchCount > 0;
    const creatorId = mode === "create" ? authenticatedUser?.id : campaign?.createdById;
    const bookerId = mode === "create" ? booker?.id : campaign?.booker?.uid;
    const isBookerPitchDigestShown = canSendBookerPitchDigest(creatorId, bookerId);
    const reviewer = isPitchReviewer(authenticatedUser, {
        createdById: campaign?.createdById ?? authenticatedUser?.id,
        booker: campaign?.booker ?? (booker?.id ? { uid: booker.id } : undefined),
    });

    useEffect(() => {
        if (mode !== "edit" || !campaignId) {
            setCampaignLoading(false);
            return;
        }

        let cancelled = false;

        async function load() {
            setCampaignLoading(true);
            try {
                const loaded = await getCampaignById(campaignId!);
                const pitches = await getPitchesByCampaignId(campaignId!);
                if (cancelled) {
                    return;
                }
                setCampaign(loaded);
                setPitchCount(loaded.pitchCount || pitches.length);
                applyCampaignToForm(loaded, reset, {
                    setBooker,
                    setCurrency,
                    setCurrencyLockedFromBooker,
                    setCampaignType,
                    setPlatforms,
                    setEligibility,
                    setShowNotesAndRates,
                    setSendBookerPitchDigest,
                    setFileUrl,
                });
            } catch (error) {
                console.error(error);
                analyticsService().logError(error);
                toast.add({
                    type: "error",
                    title: "Couldn’t load this opportunity",
                    description: "Refresh and try again.",
                });
            } finally {
                if (!cancelled) {
                    setCampaignLoading(false);
                }
            }
        }

        void load();
        return () => {
            cancelled = true;
        };
    }, [mode, campaignId, reset]);

    useEffect(() => {
        if (mode !== "edit" || !campaign?.booker?.uid) {
            return;
        }
        const matched = bookers.find((item) => item.id === campaign.booker?.uid);
        if (matched) {
            setBooker(matched);
        }
    }, [bookers, campaign, mode]);

    useEffect(() => {
        if (mode === "edit" || !booker) {
            return;
        }
        applyBookerCurrency(booker, setCurrency, setCurrencyLockedFromBooker);
    }, [booker, mode]);

    useEffect(() => {
        if (!isSalesOrAdmin) {
            return;
        }
        setValue("leadSource", campaign?.leadSource ?? []);
        setValue("crmLeadId", campaign?.crmLeadId ?? "");
        setValue("salesTeamId", asIdList(campaign?.salesTeamId));
    }, [isSalesOrAdmin, campaign, setValue]);

    const currencySymbol = getCurrencySymbol(currency || "USD") || "$";
    const breadcrumbName =
        campaignName?.trim() ||
        campaign?.campaignName ||
        (mode === "create" ? "New Campaign" : "Campaign Name");
    const submitLabel = mode === "create" ? "Publish" : "Save";

    async function selectBooker(next?: User) {
        if ((next && next.id) || !next) {
            setBooker(next);
            return;
        }

        const res = await createBooker(next, authenticatedUser);
        if (res.success) {
            queryClient.refetchQueries({ queryKey: ["bookers"] });
            setBooker({ ...next, id: res.result, clientId: res.clientId });
        } else {
            console.error(res.error);
            toast.add({
                type: "error",
                title: "Couldn’t create booker",
                description: `${res.error} — ${next.email}. Please contact Lyonel and Tim.`,
            });
        }
    }

    function togglePlatform(platform: Platform, checked: boolean) {
        setPlatforms((current) => {
            const next = checked
                ? [...current, platform]
                : current.filter((item) => item !== platform);
            setEligibility((value) => syncBrowseFilterPlatforms(value, next));
            return next;
        });
        setBasicsErrors((current) => ({ ...current, platforms: undefined }));
    }

    async function submitForm(formData: CampaignFormValues) {
        const nextBasicsErrors = {
            booker: booker?.id ? undefined : "Booker is required.",
            platforms: platforms.length ? undefined : "Select at least one platform.",
        };
        setBasicsErrors(nextBasicsErrors);

        if (nextBasicsErrors.booker || nextBasicsErrors.platforms) {
            toast.add({
                type: "error",
                title: "Fix the highlighted fields",
                description: "Campaign basics need a booker and at least one platform.",
            });
            return;
        }

        if (mode === "create" && !canCreateCampaign(authenticatedUser)) {
            toast.add({
                type: "error",
                title: "You can’t create an opportunity",
            });
            return;
        }

        if (mode === "edit" && campaign && !isPitchReviewer(authenticatedUser, campaign)) {
            toast.add({
                type: "error",
                title: "You can’t edit these settings",
            });
            return;
        }

        const minPrice = formData.minPricePerCreator;
        const maxPrice = formData.maxPricePerCreator;
        if (openAfterSave) {
            const missing = missingFieldsToOpenPitching({ details: formData.details, currency });
            if (missing.length) {
                const copy = pitchingOpenRequirementsCopy(missing);
                if (missing.includes("currency")) {
                    setBasicsErrors((current) => ({
                        ...current,
                        currency: "Currency is required.",
                    }));
                }
                toast.add({
                    type: "warning",
                    title: copy.title,
                    description: copy.description,
                });
                return;
            }
        }
        if (
            typeof minPrice === "number" &&
            typeof maxPrice === "number" &&
            !Number.isNaN(minPrice) &&
            !Number.isNaN(maxPrice) &&
            maxPrice < minPrice
        ) {
            toast.add({
                type: "error",
                title: "Fix the highlighted fields",
                description: "Max price must be greater than or equal to min price.",
            });
            return;
        }

        const fileList =
            formData.file instanceof FileList && formData.file.length > 0
                ? formData.file
                : undefined;

        const nextEligibility = getEligibilityFromBrowseFilter(syncBrowseFilterPlatforms(eligibility, platforms));

        if (mode === "create") {
            const campaignDoc = buildCampaignFromForm({
                mode: "create",
                formData,
                authenticatedUser,
                booker: booker!,
                currency,
                campaignType,
                platforms,
                eligibility: nextEligibility,
                showNotesAndRates,
                sendBookerPitchDigest,
                salesTeam,
                basicsLocked: false,
            });
            const result = await newCreateOrUpdateCampaign(campaignDoc, [], fileList);
            if (!result.success) {
                console.error(result.error);
                toast.add({
                    type: "error",
                    title: "Couldn’t publish",
                    description: "Try again, or contact Tim if this keeps happening.",
                });
                return;
            }

            analyticsService().logEvent("Created Opportunity", { ...campaignDoc, bookings: undefined });
            navigate(`/campaign/${campaignDoc.id}`);
            return;
        }

        if (!campaignId || !campaign) {
            return;
        }

        const patch = buildCampaignFromForm({
            mode: "edit",
            existing: campaign,
            formData,
            authenticatedUser,
            booker: booker!,
            currency,
            campaignType,
            platforms,
            eligibility: nextEligibility,
            showNotesAndRates,
            sendBookerPitchDigest,
            salesTeam,
            basicsLocked,
            openAfterSave,
        });

        const excluded = await countNewlyExcludedPitches({
            campaignId,
            previousEligibility: campaign.eligibility,
            nextEligibility,
            platforms: campaign.campaignPlatform || platforms,
        });
        if (excluded > 0) {
            toast.add({
                type: "warning",
                title: `${excluded} existing pitch${excluded === 1 ? "" : "es"} no longer match`,
                description: "Those pitches were not locked or rejected.",
            });
        }

        const result = await updateCampaignSettings(campaignId, patch, fileList);
        if (!result.success) {
            console.error(result.error);
            toast.add({
                type: "error",
                title: "Couldn’t save",
                description: "Try again, or contact Tim if this keeps happening.",
            });
            return;
        }

        analyticsService().logEvent("Updated Opportunity", { ...campaign, ...patch, bookings: undefined });

        if (openAfterSave) {
            toast.add({
                type: "success",
                title: "Opened for pitches",
            });
            navigate(`/campaign/${campaignId}`);
            return;
        }

        const nextFileUrl = result.result ?? campaign.fileUrl;
        setCampaign({ ...campaign, ...patch, ...(nextFileUrl !== undefined ? { fileUrl: nextFileUrl } : {}) } as Campaign);
        setFileUrl(nextFileUrl);
        resetField("file");
        toast.add({
            type: "success",
            title: "Settings saved",
        });
    }

    return {
        loading: mode === "edit" && campaignLoading,
        shell: {
            breadcrumbName,
            campaignHref: mode === "edit" && campaignId ? `/campaign/${campaignId}` : undefined,
            isSubmitting,
            submitLabel,
            onSubmit: handleSubmit(submitForm),
        },
        basics: {
            isManager,
            booker,
            bookers,
            clients,
            bookerLocked,
            basicsLocked,
            basicsErrors,
            onBookerSelect: (next?: User) => {
                void selectBooker(next);
                setBasicsErrors((current) => ({
                    ...current,
                    booker: undefined,
                }));
            },
            currency,
            currencyLockedFromBooker,
            onCurrencyChange: (next: Currency) => {
                setCurrency(next);
                setBasicsErrors((current) => ({ ...current, currency: undefined }));
            },
            campaignNameError: errors.campaignName ? "Campaign name is required." : undefined,
            register,
            platforms,
            onTogglePlatform: togglePlatform,
            campaignType,
            onCampaignTypeChange: setCampaignType,
        },
        brief: {
            campaignType,
            errors,
            register,
            currencySymbol,
            fileUrl,
            onReplaceFile: () => setFileUrl(undefined),
        },
        eligibility: {
            eligibility,
            onChange: setEligibility,
            platforms,
            currency,
        },
        extras: {
            control,
            errors,
            register,
            showNotesAndRates,
            onShowNotesAndRatesChange: setShowNotesAndRates,
            sendBookerPitchDigest,
            onSendBookerPitchDigestChange: setSendBookerPitchDigest,
            isBookerPitchDigestShown,
            reviewer,
            isSalesOrAdmin,
            salesTeam,
        },
    };
}
